import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/lib/supabase';

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
}

export default function BillingSummaryCard() {
  const { profile } = useAuth();
  const [totalSpent, setTotalSpent] = useState(0);
  const [lastPayment, setLastPayment] = useState<{ amount: number; type: string; date: string } | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchBilling = async () => {
      if (!profile?.id) {
        setLoading(false);
        return;
      }
      try {
        const { data, error } = await supabase
          .from('payments')
          .select('amount, payment_type, created_at, status')
          .eq('user_id', profile.id)
          .eq('status', 'approved')
          .order('created_at', { ascending: false })
          .limit(20);

        if (error) throw error;

        const payments = data || [];
        const total = payments.reduce((sum, p) => sum + Number(p.amount), 0);
        setTotalSpent(total);

        if (payments.length > 0) {
          setLastPayment({
            amount: Number(payments[0].amount),
            type: payments[0].payment_type,
            date: payments[0].created_at,
          });
        }
      } catch {
        // Silently fail — card shows zeros
      } finally {
        setLoading(false);
      }
    };

    fetchBilling();
  }, [profile?.id]);

  if (loading) {
    return (
      <div className="bg-white dark:bg-background-100 rounded-2xl border border-background-200 dark:border-background-300 p-5 animate-pulse">
        <div className="h-4 bg-background-200 rounded w-1/3 mb-4" />
        <div className="grid grid-cols-2 gap-4">
          <div className="h-8 bg-background-200 rounded" />
          <div className="h-8 bg-background-200 rounded" />
        </div>
      </div>
    );
  }

  const typeLabels: Record<string, string> = {
    plan: 'Plano',
    credits: 'Créditos',
    excess: 'Excedente',
    setup: 'Implantação',
  };

  return (
    <div className="bg-white dark:bg-background-100 rounded-2xl border border-background-200 dark:border-background-300 p-5">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-emerald-50 dark:bg-emerald-500/10 flex items-center justify-center flex-shrink-0">
            <i className="ri-bank-card-line text-xl text-emerald-600 dark:text-emerald-400" />
          </div>
          <div>
            <p className="text-sm font-semibold text-foreground-900 dark:text-foreground-100">
              Resumo Financeiro
            </p>
            <p className="text-xs text-foreground-500">
              {lastPayment
                ? `Último pagamento: ${typeLabels[lastPayment.type] || lastPayment.type} em ${new Date(lastPayment.date).toLocaleDateString('pt-BR')}`
                : 'Nenhum pagamento registrado ainda'}
            </p>
          </div>
        </div>
        <Link
          to="/invoices"
          className="text-xs font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 transition-colors flex items-center gap-1 whitespace-nowrap"
        >
          Ver faturas
          <i className="ri-arrow-right-line text-[10px]" />
        </Link>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="p-3 rounded-xl bg-background-50 dark:bg-background-200 border border-background-200 dark:border-background-300 text-center">
          <p className="text-lg font-heading font-bold text-foreground-950 dark:text-foreground-100 tabular-nums">
            {formatCurrency(totalSpent)}
          </p>
          <p className="text-[10px] text-foreground-500">Total investido</p>
        </div>
        <div className="p-3 rounded-xl bg-background-50 dark:bg-background-200 border border-background-200 dark:border-background-300 text-center">
          <p className="text-lg font-heading font-bold text-foreground-950 dark:text-foreground-100 tabular-nums">
            {lastPayment ? formatCurrency(lastPayment.amount) : '—'}
          </p>
          <p className="text-[10px] text-foreground-500">
            {lastPayment ? 'Último pagamento' : 'Sem pagamentos'}
          </p>
        </div>
      </div>
    </div>
  );
}